import { useContext, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { UserContext } from "src/Utils/Context/UserContext";
import {
  useOrganizationRoles,
  useCreateOrganizationRole,
  useDeleteOrganizationRole,
} from "src/data/organization/queries/staffRole";
import { isOrganizationManageEnabled } from "src/helpers/FeatureCapabilityConfig";
import { StaffRoleDto } from "src/data/organization/types/staffRole";
import { InfoCard } from "src/design/InfoCard";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { SnackBar } from "src/design/SnackBar";
import {
  colorTextPrimary,
  colorTextMuted,
  colorAccent,
  colorSurfaceHover,
  colorActionDanger,
  colorSurfaceDangerHover,
  colorTextError,
} from "src/design/tokens";

const RolesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const organizationId = user?.organizationId ?? null;
  const canManage = isOrganizationManageEnabled(user);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [pendingDelete, setPendingDelete] = useState<StaffRoleDto | null>(null);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data: roles, isLoading } = useOrganizationRoles();

  const createMutation = useCreateOrganizationRole((role) => {
    setCreateOpen(false);
    setCreateName("");
    setCreateDescription("");
    setSnack({
      open: true,
      message: t("myOrganization.roles.createSuccess", { name: role.name }),
      severity: "success",
    });
  });

  const deleteMutation = useDeleteOrganizationRole(() => {
    setSnack({
      open: true,
      message: t("myOrganization.roles.deleteSuccess", {
        name: pendingDelete?.name,
      }),
      severity: "success",
    });
    setPendingDelete(null);
  });

  const handleCreate = () => {
    console.log(
      "Creating role with name:",
      createName,
      "and description:",
      createDescription,
      "for organization ID:",
      organizationId,
    );

    if (!createName.trim() || !organizationId) return;
    createMutation.mutate(
      {
        name: createName.trim(),
        description: createDescription.trim() || undefined,
        organizationId,
      },
      {
        onError: (err) =>
          setSnack({
            open: true,
            message: err instanceof Error ? err.message : t("common.error"),
            severity: "error",
          }),
      },
    );
  };

  const handleDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onError: (err) =>
        setSnack({
          open: true,
          message: err instanceof Error ? err.message : t("common.error"),
          severity: "error",
        }),
    });
  };

  return (
    <>
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: colorTextPrimary }}
            >
              {t("myOrganization.roles.pageTitle")}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", justifyContent: { md: "flex-end" } }}
          >
            {canManage && (
              <CustomButton
                color="black"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                {t("myOrganization.roles.createRole")}
              </CustomButton>
            )}
          </Grid>
        </Grid>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <InfoCard
            title={`${t("myOrganization.roles.pageTitle")} (${(roles ?? []).length})`}
            icon={<WorkOutlineIcon fontSize="small" />}
          >
            {(roles ?? []).length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" sx={{ color: colorTextMuted }}>
                  {t("myOrganization.roles.noRoles")}
                </Typography>
              </Box>
            ) : (
              <Table size="small" sx={{ mt: -1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ color: colorTextMuted, fontWeight: 600, pl: 0 }}
                    >
                      {t("myOrganization.roles.name")}
                    </TableCell>
                    <TableCell sx={{ color: colorTextMuted, fontWeight: 600 }}>
                      {t("myOrganization.roles.description")}
                    </TableCell>
                    <TableCell sx={{ color: colorTextMuted, fontWeight: 600 }}>
                      {t("myOrganization.roles.permissions")}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 0 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(roles ?? []).map((role) => (
                    <TableRow
                      key={role.id}
                      onClick={() => navigate(role.id)}
                      sx={{
                        cursor: "pointer",
                        "&:last-child td": { borderBottom: 0 },
                        "&:hover": { backgroundColor: colorSurfaceHover },
                      }}
                    >
                      <TableCell
                        sx={{ pl: 0, fontWeight: 500, color: colorTextPrimary }}
                      >
                        {role.name}
                      </TableCell>
                      <TableCell sx={{ color: colorTextMuted }}>
                        {role.description ?? "—"}
                      </TableCell>
                      <TableCell sx={{ color: colorTextMuted }}>
                        {role.permissions.length}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ pr: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 0.5,
                          }}
                        >
                          {canManage && (
                            <Tooltip title={t("common.delete")}>
                              <IconButton
                                size="small"
                                onClick={() => setPendingDelete(role)}
                                sx={{
                                  color: colorTextMuted,
                                  "&:hover": {
                                    color: colorActionDanger,
                                    backgroundColor: colorSurfaceDangerHover,
                                  },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <ChevronRightIcon
                            sx={{ color: colorAccent, fontSize: 20 }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </InfoCard>
        )}
      </Box>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t("myOrganization.roles.createRole")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              label={t("myOrganization.roles.name")}
              fullWidth
              size="small"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              required
            />
            <TextField
              label={t("myOrganization.roles.description")}
              fullWidth
              size="small"
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              placeholder={t("myOrganization.roles.descriptionPlaceholder")}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton
            color="inverted"
            onClick={() => setCreateOpen(false)}
            disabled={createMutation.isPending}
          >
            {t("common.cancel")}
          </CustomButton>
          <CustomLoadingButton
            loading={createMutation.isPending}
            disabled={!createName.trim()}
            onSubmit={handleCreate}
            label={t("myOrganization.roles.createRole")}
            color="success"
            fullwidth={false}
          />
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!pendingDelete}
        onClose={() => !deleteMutation.isPending && setPendingDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t("myOrganization.roles.deleteConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("myOrganization.roles.deleteConfirmBody", {
              name: pendingDelete?.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton
            color="inverted"
            onClick={() => setPendingDelete(null)}
            disabled={deleteMutation.isPending}
          >
            {t("common.cancel")}
          </CustomButton>
          <CustomButton
            disabled={deleteMutation.isPending}
            onClick={handleDelete}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{
              backgroundColor: colorActionDanger,
              color: "#fff",
              "&:hover": { backgroundColor: colorTextError },
            }}
          >
            {t("common.delete")}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};

export default RolesList;
